import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Search, Plus, X, Check, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { fmtNum } from "@/lib/datasetFmt";

const emptyForm = () => ({ jugador: "", anio_nacimiento: "", posicion: "", rival_actual: "", codigo_rffm: "" });

export default function FeaturedRivalsPicker({ matchId, matchRival = "", disabled = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    api.get(`/matches/${matchId}/featured-rivals`)
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, [matchId]);

  useEffect(() => {
    if (!open) return;
    setSearching(true);
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.append("q", q.trim());
      if (matchRival) params.append("rival", matchRival);
      api.get(`/rival-dataset/search?${params.toString()}`)
        .then((r) => setResults(r.data || []))
        .catch((err) => toast.error(formatApiError(err)))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q, matchRival]);

  const saveItems = async (jids) => {
    try {
      const r = await api.put(`/matches/${matchId}/featured-rivals`, {
        items: jids.map((jugador_id) => ({ jugador_id })),
      });
      setItems(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const selectedIds = items.map((i) => i.jugador_id);

  const addPlayer = (p) => {
    if (selectedIds.includes(p.jugador_id)) return;
    saveItems([...selectedIds, p.jugador_id]);
    toast.success(`${p.jugador} añadido como destacado`);
  };

  const removePlayer = (jid) => saveItems(selectedIds.filter((x) => x !== jid));

  const createNew = async (e) => {
    e.preventDefault();
    const payload = {
      jugador: form.jugador.trim(),
      anio_nacimiento: form.anio_nacimiento ? parseInt(form.anio_nacimiento, 10) : null,
      posicion: form.posicion.trim(),
      rival_actual: (form.rival_actual || matchRival).trim(),
      codigo_rffm: form.codigo_rffm.trim(),
    };
    if (!payload.jugador) { toast.error("Nombre obligatorio"); return; }
    try {
      const r = await api.post("/rival-dataset/players", payload);
      const player = r.data.player;
      if (r.data.duplicate) toast.info(`Ya existía: se usa la ficha de ${player.jugador}`);
      else toast.success("Jugador rival creado");
      addPlayer(player);
      setCreating(false);
      setForm(emptyForm());
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-2" data-testid="featured-rivals-picker">
      {/* Selected chips */}
      <div className="flex flex-wrap gap-2">
        {items.length === 0 && (
          <span className="text-xs text-slate-400 italic">Sin jugadores destacados rivales.</span>
        )}
        {items.map((it) => (
          <span
            key={it.jugador_id}
            className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-purple-100 text-purple-900 text-xs font-bold"
            data-testid={`featured-chip-${it.jugador_id}`}
          >
            <Star className="h-3 w-3" style={{ color: "#D9B300" }} />
            <button
              type="button"
              className="hover:underline"
              onClick={() => navigate(`/rivals/jugador/${it.jugador_id}`)}
              data-testid={`featured-chip-link-${it.jugador_id}`}
            >
              {it.player?.jugador}
            </button>
            {!disabled && (
              <button
                type="button"
                onClick={() => removePlayer(it.jugador_id)}
                className="w-4 h-4 rounded-full bg-purple-200 hover:bg-purple-300 grid place-items-center"
                data-testid={`featured-chip-remove-${it.jugador_id}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>

      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { setQ(""); setCreating(false); setOpen(true); }}
          className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
          data-testid="featured-add-button"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Seleccionar jugador rival
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Jugador destacado rival</DialogTitle>
          </DialogHeader>

          {!creating ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre, equipo rival, posición o año"
                  className="pl-9"
                  data-testid="featured-search"
                />
              </div>
              {matchRival && !q.trim() && (
                <p className="text-[11px] text-slate-400">
                  Mostrando primero jugadores de <b>{matchRival}</b>.
                </p>
              )}
              <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-100">
                {searching ? (
                  <div className="p-3 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando...
                  </div>
                ) : results.length === 0 ? (
                  <div className="p-4 text-xs text-slate-400 italic text-center">
                    No se han encontrado jugadores.
                  </div>
                ) : (
                  results.map((p) => {
                    const selected = selectedIds.includes(p.jugador_id);
                    return (
                      <button
                        key={p.jugador_id}
                        type="button"
                        onClick={() => (selected ? removePlayer(p.jugador_id) : addPlayer(p))}
                        className={`w-full text-left px-3 py-2.5 hover:bg-purple-50 flex items-center gap-2 ${selected ? "bg-purple-50" : ""}`}
                        data-testid={`featured-result-${p.jugador_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                            {selected && <Check className="h-3.5 w-3.5 text-purple-600 shrink-0" />}
                            {p.jugador}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{p.rival_actual || "Sin club"}</div>
                          <div className="text-[11px] text-slate-400">
                            {p.posicion || "Sin posición"} · {p.anio_nacimiento || "—"}
                            {p.ranking_general != null && ` · Ranking #${p.ranking_general}`}
                            {p.goles_total != null && ` · ${fmtNum(p.goles_total)} goles`}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
              <Button
                type="button"
                onClick={() => {
                  setForm({ ...emptyForm(), jugador: q.trim(), rival_actual: matchRival });
                  setCreating(true);
                }}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                data-testid="featured-new-player"
              >
                <Plus className="h-4 w-4 mr-2" /> Nuevo jugador rival
              </Button>
            </div>
          ) : (
            <form onSubmit={createNew} className="space-y-3" data-testid="featured-new-form">
              <p className="text-xs text-slate-500">
                Antes de crear, comprueba que el jugador no aparece en la búsqueda para evitar duplicados.
              </p>
              <div>
                <Label className="text-xs">Nombre *</Label>
                <Input value={form.jugador} onChange={(e) => setForm({ ...form, jugador: e.target.value })} required data-testid="featured-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Año nacimiento</Label>
                  <Input type="number" min="1900" max="2099" value={form.anio_nacimiento} onChange={(e) => setForm({ ...form, anio_nacimiento: e.target.value })} data-testid="featured-form-year" />
                </div>
                <div>
                  <Label className="text-xs">Posición</Label>
                  <Input value={form.posicion} onChange={(e) => setForm({ ...form, posicion: e.target.value })} data-testid="featured-form-position" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Club rival</Label>
                <Input value={form.rival_actual} onChange={(e) => setForm({ ...form, rival_actual: e.target.value })} placeholder={matchRival || "Club rival"} data-testid="featured-form-rival" />
              </div>
              <div>
                <Label className="text-xs">Código RFFM (opcional)</Label>
                <Input value={form.codigo_rffm} onChange={(e) => setForm({ ...form, codigo_rffm: e.target.value })} data-testid="featured-form-code" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setCreating(false)} data-testid="featured-form-cancel">
                  Cancelar
                </Button>
                <Button type="submit" size="sm" className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="featured-form-save">
                  Crear y destacar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
